/**
 * What an item is — T126.
 *
 * An **item** is a thing that can sit in a slot. A **block** is a thing that
 * occupies a cell in the world. They are two different lists that overlap:
 * a berry is an item and never a block, móberg is both, and most of the 32
 * blocks are landscape nobody will ever carry.
 *
 * ## 🔴 The key is the identity. The number is only a representation.
 *
 * `key` — `"kraekiber"` — is what an item *is*. `id` is a compact stand-in for
 * it on the wire and in saved inventories, pinned per key in
 * [`data/item-lock.json`](../data/item-lock.json), which is **committed and
 * append-only**.
 *
 * **This is T80's lesson, one level up, and the project has already paid for it
 * once.** Block ids used to come from a block's position in a list, so
 * inserting one renumbered everything after it and would have silently rewritten
 * every saved edit. Items get the lock **with the first item**, not after the
 * tenth — because the failure is invisible: nothing crashes, the numbers still
 * add up, and everything anybody was carrying is quietly something else.
 *
 * ⚠️ **Ids are not written in `items.json` at all.** They are joined in from the
 * lock at load time, so the two files cannot disagree with each other. A key
 * missing from the lock throws here, at startup, rather than shipping an item
 * that has no identity on the wire.
 *
 * ## Where the numbers live
 *
 * A number belonging to **one item** — what a berry restores, how long a log
 * burns — is in `items.json`. A number applying to **the whole game** — the
 * stove's cooking time, how fast you get hungry — is in
 * [`data/tuning.json`](../data/tuning.json) (**D69**, T136). Both are data
 * files that can be edited without touching code (**D11**).
 *
 * ## ⚠️ Raw and cooked are two items, not one item with a state
 *
 * 🎯 **haugur, 2026-08-13**, asked directly: *"2, if you put in the work to cook
 * it you get rewarded."* So **D71**'s ladder — raw → cooked → preserved — is
 * three rows in `items.json`, each with its own numbers. There is no state field
 * and there should never be one: a state field is a small machine that every
 * later system has to understand, and three rows in a list is not.
 *
 * ## ⚠️ Why the names are here and not in `en.json`
 *
 * Same rule blocks already follow: **these are names, not vocabulary.** They
 * stay in both locales, because the Icelandic word *is* the content (`birki`,
 * `krækiber`). 🔴 **Any SENTENCE about an item is different** and binds to
 * standards §8b: it goes in `en.json` as a whole sentence with a hole in it,
 * never `'You picked up ' + name`, which cannot be made grammatical in
 * Icelandic because the noun changes shape inside the sentence.
 */

import itemsData from '../data/items.json' with { type: 'json' };
import lockData from '../data/item-lock.json' with { type: 'json' };
import { TUNING } from './tuning.ts';
import { blockById, isPickedByHand, type HeldTool } from './blocks.ts';

/**
 * Where an item is allowed to be kept.
 *
 * 🎯 **haugur:** *"you can't store food in a chest, you see."* The `búr` exists
 * precisely because food needs somewhere that is not your pocket and not a
 * chest, and the `hlaða` is the barn the world itself draws fodder from.
 */
export type StorageCategory = 'food' | 'general' | 'fodder';

/**
 * What eating one of these does.
 *
 * 🔴 **It fills the FOOD bar, and it used to say health.** These two numbers
 * have not changed — the bar they describe has. haugur's notes were written
 * when there was one bar; **D79 gave him the two he asked for**, and on
 * 2026-08-20 he named the link between them: *"burn food to rebuild health."*
 * So a berry fills food, and food is what buys health back — `vitalsAfterEating`
 * in [`vitals.ts`](./vitals.ts).
 */
export interface EdibleDef {
  /** How much of the food bar one of these gives back. */
  readonly restoresPercent: number;
  /**
   * The most this food can take you to, however much you eat.
   *
   * 🔴 **This is what makes cooking worth doing.** Raw food has a ceiling of 50,
   * so it cannot carry you past half however much of it you swallow; cooked food
   * reaches 100. **D71** — the value scales, the food never spoils.
   *
   * ⚠️ **And it creates a message the screen has to show.** Eating at 60% with a
   * ceiling of 50 does nothing, and *"I ate and nothing happened"* is a bug
   * report. **D64** already requires the screen to NAME the reason, exactly as
   * it does for *"Exposed — wind, no shelter"*.
   */
  readonly ceilingPercent: number;
}

export interface FuelDef {
  /** ⏱️ **GAME** hours. One game hour is 50 real seconds. */
  readonly burnGameHours: number;
  /** The item key this leaves behind when it has burned, if any. */
  readonly leavesBehind?: string;
}

/**
 * 👕 **The four parts of the body something can be worn on** (T153).
 *
 * 🎯 **haugur, 2026-08-19**, with a picture of Minecraft's inventory: *"we need
 * that way we can clothe parts of the body, i can put on a hat, or a sweater or
 * better shoes."*
 *
 * ⚠️ **Four, because four is what a person divides into** — not because
 * Minecraft has four. `Development/Minecraft/09` §3 marks the count of slots as
 * **flavour**; what is load-bearing there is that armour is worn per body part
 * and that coverage decides how much each piece is worth.
 */
export type BodyPart = 'head' | 'body' | 'legs' | 'feet';

export interface ItemDef {
  /** From the lock, never from this item's position in the list. */
  readonly id: number;
  readonly key: string;
  /** English name (D7 — English first). */
  readonly name: string;
  /** Icelandic name. A name, not vocabulary — it stays in both locales. */
  readonly nameIs: string;
  readonly storage: StorageCategory;
  /**
   * The block key this places, if it places one at all.
   *
   * 🔴 **Absent means it cannot be built with.** 🎯 **haugur:** *"food blocks
   * cannot be used as building items."* That is the whole of it — no separate
   * flag, because a thing that places nothing is exactly a thing you cannot
   * build with.
   */
  readonly placesBlock?: string;
  /**
   * 🏠 **Older blocks that still give this item back** — blocks nothing places
   * any more, which may still stand in somebody's world. D135: the turf-house
   * `vefstofa`, built before the item began placing the pit house.
   */
  readonly alsoDroppedBy?: readonly string[];
  readonly edible?: EdibleDef;
  readonly fuel?: FuelDef;
  /**
   * 👕 Where on the body this is worn, if it is worn at all (T153).
   *
   * 🔴 **Absent means it is not clothing**, exactly as an absent
   * {@link ItemDef.placesBlock} means it cannot be built with — one field, no
   * separate flag. It is what a clothing slot checks before it will take
   * something, so a lump of móberg cannot be worn as a hat.
   *
   * ⚠️ **No item declares it yet, and that is not an oversight.** There is no
   * wool in the game, no vaðmál and no way to make either; garments arrive with
   * the cold system that gives them a job (**T131**, ADR-0016 §5). This is the
   * rule the four slots are built on, waiting for the first thing that fits it.
   */
  readonly wears?: BodyPart;
  /** What the kaupskip will give for one. ⚠️ Provisional — nothing trades yet. */
  readonly tradeValue?: number;
  /** How many fit in one slot. Defaults to `tuning.json`'s `defaultStackSize`. */
  readonly stack: number;
  /**
   * 🪓⛏️🥄 **What this is FOR and how good it is**, if it is a tool at all.
   *
   * 🔴 **Absent means it is not a tool** — the same one-field rule
   * {@link ItemDef.placesBlock} and {@link ItemDef.wears} already follow.
   */
  readonly tool?: HeldTool;
  /**
   * 🔨 **How many blocks it breaks before it is gone.** Absent means it never
   * wears out — which is every item that is not a tool.
   *
   * 🔴 **Anything with this MUST have `stack: 1`.** Two tools in one slot cannot
   * carry two different amounts of wear. `Minecraft/09` §2.2 states the same
   * rule, and a test enforces it here rather than trusting the author.
   */
  readonly durability?: number;
  /**
   * 🍲 **What one of these becomes over a fire**, if it can be cooked at all.
   *
   * 🔴 **Absent means it cannot be cooked** — the same one-field rule
   * {@link ItemDef.placesBlock}, {@link ItemDef.wears} and {@link ItemDef.tool}
   * already follow. **T222, and it is deliberately the shape of
   * {@link FuelDef.leavesBehind}**: a fire turns one named item into another
   * named item, and both halves of that sentence live on the thing going in.
   *
   * 🔴 **The count never changes — D115.** One raw fish is one cooked fish, and
   * what cooking buys is the VALUE: `restoresPercent` doubles and
   * `ceilingPercent` goes 50 → 100 (**D71**), which is the whole reason a pack
   * full of raw fish cannot take you past half the bar.
   *
   * ⚠️ **Nothing here says how LONG it takes.** That is one number for the whole
   * game — `tuning.json`'s `cooking.cookGameHours` — because it is a property of
   * the fire rather than of the fish. 👉 **The day one food should cook slower
   * than another, this is where that number would arrive**, beside the name of
   * what it becomes.
   */
  readonly cooksTo?: string;
  readonly note?: string;
}

/** An empty slot. No item may ever take id 0 — see the lock file. */
export const ITEM_NONE = 0;

interface AuthoredItem {
  readonly key: string;
  readonly name: string;
  readonly nameIs: string;
  readonly storage: string;
  readonly placesBlock?: string;
  readonly alsoDroppedBy?: readonly string[];
  readonly edible?: EdibleDef;
  readonly fuel?: FuelDef;
  readonly wears?: BodyPart;
  readonly tradeValue?: number;
  readonly stack?: number;
  readonly tool?: HeldTool;
  readonly durability?: number;
  readonly cooksTo?: string;
  readonly note?: string;
}

const LOCKED_IDS: ReadonlyMap<string, number> = new Map(Object.entries(lockData.ids));

export const ITEMS: readonly ItemDef[] = (itemsData.items as readonly AuthoredItem[]).map((it) => {
  const id = LOCKED_IDS.get(it.key);
  /* Loud, and at startup rather than in the middle of a game. An item with no
     locked id has no identity on the wire, so there is nothing sensible to fall
     back to — and a silent fallback here is the exact failure the lock exists
     to prevent. */
  if (id === undefined) {
    throw new Error(
      `item "${it.key}" is not in item-lock.json. Append it to the bottom of the lock; never insert or renumber.`,
    );
  }
  return {
    ...it,
    id,
    storage: it.storage as StorageCategory,
    stack: it.stack ?? TUNING.inventory.defaultStackSize,
  };
});

/** Item id by key — `ITEM.kraekiber` rather than a magic number. */
export const ITEM: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(ITEMS.map((i) => [i.key, i.id])),
);

/* Maps rather than plain objects indexed by a value off the wire — the same
   object-injection reasoning as blocks.ts, answered rather than suppressed. */
const BY_ID: ReadonlyMap<number, ItemDef> = new Map(ITEMS.map((i) => [i.id, i]));
const BY_KEY: ReadonlyMap<string, number> = new Map(ITEMS.map((i) => [i.key, i.id]));

export function itemById(id: number): ItemDef | undefined {
  return BY_ID.get(id);
}

/** An item key to its id, or `undefined`. For keys arriving from a data file. */
/**
 * 👕 Where this item is worn, or `undefined` if it is not clothing.
 *
 * ⚠️ **Today it answers `undefined` for every item in the game**, which is why
 * the four clothing slots cannot be filled yet. See {@link ItemDef.wears}.
 */
export function wornAt(itemKey: string): BodyPart | undefined {
  const id = itemIdForKey(itemKey);
  return id === undefined ? undefined : itemById(id)?.wears;
}

export function itemIdForKey(key: string): number | undefined {
  return BY_KEY.get(key);
}

/**
 * The block this item places, or `undefined` if it places nothing.
 *
 * Returns the **key**, not the id, so a data file naming a block that does not
 * exist degrades visibly rather than resolving to air and deleting terrain.
 */
export function blockPlacedBy(id: number): string | undefined {
  return BY_ID.get(id)?.placesBlock;
}

/** Can this item be built with at all? */
export function isPlaceable(id: number): boolean {
  return BY_ID.get(id)?.placesBlock !== undefined;
}

/* The inverse of `placesBlock`, built once. A block yields the item that would
   put it back, which is the only mapping that cannot get out of step with
   itself: break a móberg and you can place a móberg. */
const DROPPED_BY_BLOCK: ReadonlyMap<string, string> = new Map(
  ITEMS.flatMap((i) => [
    ...(i.placesBlock === undefined ? [] : [[i.placesBlock, i.key] as const]),
    /* 🏠 D135 — and any older block of it still standing in somebody's world. */
    ...(i.alsoDroppedBy ?? []).map((b) => [b, i.key] as const),
  ]),
);

/**
 * What you get for breaking this block, or `undefined` for nothing at all.
 *
 * 🔴 **Derived from `placesBlock`, never authored separately.** A second list
 * saying "móberg drops móberg" is a second thing to keep true, and the first
 * time the two disagree a player either loses what they mined or duplicates it.
 * The inverse of "this item places that block" is exactly "that block yields
 * this item", and it cannot drift from itself.
 *
 * ⚠️ **Most blocks yield NOTHING, and that is correct rather than unfinished.**
 * Kjarr, lyng, grass and moss are landscape: breaking them clears them. Only
 * `birkistofn` and `moberg` are carryable today, because those are the only two
 * items with a `placesBlock` — see `items.json`, which is hand-authored on
 * purpose so that adding a block does not quietly invent an item (T126).
 *
 * ⚠️ **This does not ask whether you have the TOOL for it.** That is
 * `dropsWhenBrokenBy` in `blocks.ts`, and the caller must ask both: a grágrýti
 * broken by hand yields its item here and is still kept by nobody.
 */
export function itemDroppedByBlock(blockKey: string): string | undefined {
  return DROPPED_BY_BLOCK.get(blockKey);
}

/**
 * What eating one of this item does, or `undefined` if it is not food.
 *
 * ⚠️ **By key, because that is what a client packet carries** and what a saved
 * inventory row holds — the id is only the wire representation.
 */
export function edibleFor(itemKey: string): EdibleDef | undefined {
  const id = BY_KEY.get(itemKey);
  return id === undefined ? undefined : BY_ID.get(id)?.edible;
}

/**
 * 🍲 **What one of these becomes over a fire**, or `undefined` if it cannot be
 * cooked — **T222**.
 *
 * 🔴 **This is the whole definition of "can be cooked".** There is no list of
 * cookable items anywhere, exactly as there is no list of fuels and no list of
 * tools: an item that names what it becomes is cookable, and one that does not
 * is not. ⚠️ **The fault this avoids is the one `campfireAccepts` was written
 * around** — a hard-coded list of keys goes stale the day a fifth fish arrives
 * and nothing fails.
 *
 * ⚠️ **It does NOT check that the answer exists.** `items.test.ts` does, once,
 * at build time — which is the same division `leavesBehind` keeps, and the same
 * reason: a lookup that silently returned `undefined` for a typo would look
 * exactly like a food that is not meant to be cooked.
 */
export function cooksTo(itemKey: string): string | undefined {
  const id = BY_KEY.get(itemKey);
  return id === undefined ? undefined : BY_ID.get(id)?.cooksTo;
}

/**
 * 🪓⛏️🥄 **What you are holding, as the breaking rules want it** — or
 * `undefined` if this item is not a tool.
 *
 * ⚠️ **`undefined`, not a "hand" tool.** Bare hands are `null` in
 * {@link Held}, and an item that happens not to be a tool is the same thing as
 * having nothing in your hand for the purposes of breaking. The caller turns one
 * into the other, so this function never has to guess.
 *
 * ⚠️ **An UNKNOWN key returns `undefined` too, and does not throw.** A client
 * can name any string; a stranger holding a made-up item breaks blocks with
 * their hands, which is the safe answer.
 */
export function toolOf(itemKey: string): HeldTool | undefined {
  const id = BY_KEY.get(itemKey);
  return id === undefined ? undefined : BY_ID.get(id)?.tool;
}

/**
 * 🔨 **How many blocks this survives**, or `undefined` if it never wears out.
 *
 * Every item that is not a tool is `undefined`, and so is an unknown key.
 */
/**
 * How many of this item fit in one slot, or `undefined` for a key nobody knows.
 *
 * ⚠️ **`inventory.ts` is the caller**, and it lives there rather than here so
 * that the inventory owns the fallback for an unknown key.
 */
export function itemStackLimit(itemKey: string): number | undefined {
  const id = BY_KEY.get(itemKey);
  return id === undefined ? undefined : BY_ID.get(id)?.stack;
}

export function durabilityOf(itemKey: string): number | undefined {
  const id = BY_KEY.get(itemKey);
  return id === undefined ? undefined : BY_ID.get(id)?.durability;
}

/**
 * 🔨 **Is this tool finished?** `used` is how many blocks it has already broken.
 *
 * ✅ **False for anything that is not a tool**, so a caller may ask about
 * whatever is in the slot without checking first.
 */
export function isWornOut(itemKey: string, used: number): boolean {
  const max = durabilityOf(itemKey);
  return max !== undefined && used >= max;
}

/**
 * 🔴 **`healthAfterEating` used to live here and it has MOVED**, to
 * `vitalsAfterEating` in [`vitals.ts`](./vitals.ts).
 *
 * It is not a rename for tidiness. The old function took a health number and
 * gave a health number back, which was right for the one-bar game haugur's
 * notes describe and **wrong from the moment D79 gave him two bars**: eating
 * fills food, and food is what buys health. A function that silently applied a
 * berry to the wrong bar is exactly the class of mistake nobody on this project
 * can see from the outside (**D2**), so it was moved rather than left as a
 * working call with a stale meaning. ⚠️ **Nothing had ever called it.**
 */

/**
 * Which container kinds accept which items.
 *
 * ⚠️ **The containers themselves do not exist yet** — they are T127. What is
 * here is the *rule*, because a storage category that nothing enforces is a
 * field, not a design. 🎯 **haugur:** *"you can't store food in a chest."*
 */
export type ContainerKind = 'chest' | 'bur' | 'hlada';

/* A Map rather than an object indexed by a value that will one day arrive from
   a client packet — the same object-injection reasoning as BY_ID and BY_KEY,
   answered rather than suppressed. */
const CONTAINER_ACCEPTS: ReadonlyMap<ContainerKind, StorageCategory> = new Map([
  ['chest', 'general'],
  ['bur', 'food'],
  ['hlada', 'fodder'],
]);

export function canStoreIn(container: ContainerKind, id: number): boolean {
  const item = BY_ID.get(id);
  if (!item) return false;
  return CONTAINER_ACCEPTS.get(container) === item.storage;
}

/**
 * 🪵🌿 **What picking this block with an empty hand puts in your pack** — or
 * `undefined` if this block is not picked by hand at all.
 *
 * ## 🔴 This is the fix `rekavidur` asked for in writing
 *
 * Driftwood's own note predicted this moment:
 *
 * > *"The real fix is a flag in `block-behaviour.json` that both sides read;
 * > that is worth doing when there is a second one."*
 *
 * **There are four now** — `rekavidur`, and the three seaweeds of **T242 /
 * D120** — and the server was about to grow four near-identical branches.
 * ⚠️ ***A second way in that forgets what the first one does*** is recorded
 * five times in this repo; this is it being designed out rather than found
 * afterwards.
 *
 * ## The rule, and why it needs no table
 *
 * **A picked block gives the item of the same key.** `rekavidur` → `rekavidur`,
 * `sol` → `sol`. That is true of every one of the four, so a mapping table
 * would be four rows that can only ever drift from the keys they copy.
 *
 * ⚠️ **It returns `undefined` when no item shares the key**, rather than
 * inventing one — so flagging a block `pickedByHand` without giving it an item
 * makes the click do nothing, **which is exactly the fault the flag was created
 * to cure.** `items.test.ts` asserts the pairing instead of trusting it.
 */
export function pickedItemFor(blockId: number): string | undefined {
  if (!isPickedByHand(blockId)) return undefined;
  const key = blockById(blockId)?.key;
  if (key === undefined) return undefined;
  return itemIdForKey(key) === undefined ? undefined : key;
}
